import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, Loader2, Database, Building2, Tag, Info, ListPlus } from 'lucide-react';
import Button from '../../ui/Button';
import { createEnterpriseSystem, triggerSystemResearch, EnterpriseSystemCreate } from '../../../lib/api/enterpriseSystemsApi';
import { toast } from 'sonner';
import { useResearchQueueStore } from '../../../stores/researchQueueStore';

interface ResearchSystemModalProps {
  isOpen: boolean;
  onClose: () => void;
  systemName: string;
  existingSystemId?: string; // If system already exists in database
  onResearchStarted?: (systemName: string, jobId?: string) => void;
}

// Common enterprise system categories
const SYSTEM_CATEGORIES = [
  'ERP',
  'CRM',
  'SCM',
  'WMS',
  'MES',
  'PLM',
  'HCM',
  'BI',
  'E-Commerce',
  'Other'
];

// Common vendors by category
const VENDOR_SUGGESTIONS: Record<string, string[]> = {
  ERP: ['SAP', 'Oracle', 'Microsoft', 'Infor', 'Workday', 'NetSuite'],
  CRM: ['Salesforce', 'Microsoft', 'SAP', 'Oracle', 'HubSpot', 'Zoho'],
  SCM: ['SAP', 'Oracle', 'Kinaxis', 'Blue Yonder', 'Coupa', 'Jaggaer'],
  WMS: ['Manhattan Associates', 'Blue Yonder', 'SAP', 'Oracle', 'Infor', 'HighJump'],
  MES: ['SAP', 'Siemens', 'Rockwell', 'Dassault', 'AVEVA', 'Honeywell'],
  PLM: ['Siemens', 'Dassault', 'PTC', 'Autodesk', 'SAP', 'Oracle'],
  HCM: ['Workday', 'SAP', 'Oracle', 'ADP', 'Ceridian', 'UKG'],
  BI: ['Microsoft', 'Tableau', 'Qlik', 'SAP', 'Oracle', 'Looker'],
  'E-Commerce': ['Shopify', 'Magento', 'Salesforce', 'SAP', 'BigCommerce', 'WooCommerce'],
  Other: []
};

export const ResearchSystemModal: React.FC<ResearchSystemModalProps> = ({
  isOpen,
  onClose,
  systemName,
  existingSystemId,
  onResearchStarted
}) => {
  const navigate = useNavigate();
  const { addSystemTask } = useResearchQueueStore();
  const [vendor, setVendor] = useState('');
  const [category, setCategory] = useState('');
  const [version, setVersion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingToQueue, setIsAddingToQueue] = useState(false);
  const [suggestedVendors, setSuggestedVendors] = useState<string[]>([]);

  // Try to auto-detect category and vendor from system name
  useEffect(() => {
    if (systemName) {
      const nameLower = systemName.toLowerCase();

      // Auto-detect vendor
      const knownVendors = ['sap', 'oracle', 'microsoft', 'salesforce', 'kinaxis', 'manhattan', 'workday', 'infor'];
      for (const v of knownVendors) {
        if (nameLower.includes(v)) {
          setVendor(v.charAt(0).toUpperCase() + v.slice(1));
          break;
        }
      }

      // Auto-detect category
      if (nameLower.includes('erp') || nameLower.includes('s/4') || nameLower.includes('e-business')) {
        setCategory('ERP');
      } else if (nameLower.includes('crm') || nameLower.includes('salesforce')) {
        setCategory('CRM');
      } else if (nameLower.includes('scm') || nameLower.includes('supply chain')) {
        setCategory('SCM');
      } else if (nameLower.includes('wms') || nameLower.includes('warehouse')) {
        setCategory('WMS');
      } else if (nameLower.includes('mes') || nameLower.includes('manufacturing')) {
        setCategory('MES');
      }
    }
  }, [systemName]);

  // Update vendor suggestions when category changes
  useEffect(() => {
    if (category) {
      setSuggestedVendors(VENDOR_SUGGESTIONS[category] || []);
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!vendor.trim() || !category) {
      toast.error('Please fill in vendor and category');
      return;
    }

    setIsSubmitting(true);
    try {
      if (existingSystemId) {
        // System exists, just trigger research
        const result = await triggerSystemResearch(existingSystemId, false);
        toast.success(`Research started for ${systemName}`);
        onResearchStarted?.(systemName, result.job_id);
      } else {
        // Create new system and trigger research
        const createData: EnterpriseSystemCreate = {
          system_name: systemName,
          vendor: vendor.trim(),
          category,
          version: version.trim() || undefined,
          trigger_research: true
        };

        const result = await createEnterpriseSystem(createData);

        if (result.research_triggered) {
          toast.success(`${systemName} added to Enterprise Systems and research started!`);
          onResearchStarted?.(systemName, result.research_job_id);
        } else {
          toast.success(`${systemName} added to Enterprise Systems. Research can be triggered from the Enterprise Systems page.`);
          onResearchStarted?.(systemName);
        }
      }

      onClose();
    } catch (err: any) {
      console.error('Failed to start research:', err);
      const message = err.response?.data?.detail || err.message || 'Failed to start research';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add to research queue instead of starting immediately
  const handleAddToQueue = async () => {
    if (!existingSystemId && (!vendor.trim() || !category)) {
      toast.error('Please fill in vendor and category');
      return;
    }

    setIsAddingToQueue(true);
    try {
      await addSystemTask({
        systemId: existingSystemId,
        systemName: systemName,
        vendor: vendor.trim() || undefined,
        category: category || undefined,
      });

      toast.success(
        `Added "${systemName}" to research queue`,
        { description: 'Research will start when queue is processed' }
      );

      onClose();
      navigate('/research-queue');
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || 'Failed to add to queue';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setIsAddingToQueue(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-primary-50 to-primary-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Search className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Research System</h3>
              <p className="text-sm text-neutral-600">{systemName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {existingSystemId ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">System found in database</p>
                  <p className="text-sm text-green-700 mt-1">
                    This system already exists in the Enterprise Systems database.
                    Click "Start Research" to gather schema data.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">New system</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This system will be added to Enterprise Systems and research will be triggered automatically.
                    </p>
                  </div>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                  <Tag className="w-4 h-4 inline mr-1.5" />
                  Category *
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Select category...</option>
                  {SYSTEM_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Vendor */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                  <Building2 className="w-4 h-4 inline mr-1.5" />
                  Vendor *
                </label>
                <input
                  type="text"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  placeholder="e.g., SAP, Oracle, Microsoft"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  list="vendor-suggestions"
                />
                {suggestedVendors.length > 0 && (
                  <datalist id="vendor-suggestions">
                    {suggestedVendors.map(v => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                )}
              </div>

              {/* Version (optional) */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                  <Database className="w-4 h-4 inline mr-1.5" />
                  Version <span className="text-neutral-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="e.g., 12.2, 2023, Cloud"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting || isAddingToQueue}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddToQueue}
              disabled={isSubmitting || isAddingToQueue}
              title="Add to background queue for batch processing"
            >
              {isAddingToQueue ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <ListPlus className="w-4 h-4 mr-2" />
                  Add to Queue
                </>
              )}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || isAddingToQueue}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Start Now
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResearchSystemModal;
